/**
 * Ce que le client a envoyé pour ce mois, et ce qu'on en fait.
 *
 * POURQUOI ICI. Ces lignes arrivaient en base et n'apparaissaient nulle part :
 * le client déclarait dans le vide. Elles vivent dans « Paie du mois », juste
 * sous le lien qui les a produites, parce que c'est là qu'on traite le mois.
 *
 * RIEN N'ENTRE EN PAIE TOUT SEUL. Une ligne déclarée par un client est une
 * DEMANDE, pas une écriture. Une personne du cabinet la lit, puis dit ce qu'elle
 * en fait : portée, ou écartée. Tant qu'elle n'a rien dit, la ligne reste là.
 *
 * MAIS UNE SEULE VALIDATION SUFFIT. Décision de Rose du 09/09/2026 : « Fiona
 * doit valider une seule fois, pas deux, prime ou absence ». Porter une ligne
 * l'écrit donc DIRECTEMENT dans OpenPaye — et la reprend au passage dans la
 * saisie des variables quand c'est une prime ou des heures. D'où le libellé
 * « porter en paie » plutôt que « marquer comme porté » : le bouton fait ce
 * qu'il dit.
 *
 * Une ligne déjà partie ne repart jamais : le serveur garde la date de son
 * envoi.
 *
 * ON NE SUPPRIME PAS. « Écartée » garde la trace de ce que le client avait
 * déclaré ET de la décision de ne pas le porter — c'est exactement ce qu'on
 * cherche trois mois plus tard quand il demande pourquoi sa prime n'y est pas.
 *
 * LES LIGNES SANS RUBRIQUE SE VOIENT. Une colonne ajoutée par le client arrive
 * sans code de paie : elle est signalée « à qualifier », parce que la porter
 * demande une décision qu'aucune machine ne peut prendre à la place du cabinet.
 */

import { useEffect, useState } from 'react';
import { nomPourEnregistrer } from './nom-pour-enregistrer';
import { etiquetteTraitee, phraseDuSort } from './sort-des-lignes';
import { Check, Download, FileText, Inbox, X } from 'lucide-react';
import { api } from '../services/api';

interface PieceRecue {
  uuid: string;
  nom: string;
  /**
   * Le nom sous lequel ENREGISTRER la pièce : extension du type RECONNU par
   * le serveur, jamais celle annoncée par le client. Un fichier « %PDF… » +
   * script déposé sous « arret-maladie.bat » se télécharge en `.pdf` : sur un
   * objet local (`blob:`), le navigateur prend l'attribut `download`, pas
   * l'en-tête du serveur (verdict QA du 19/09, D2). Absent avec une API plus
   * ancienne : on retombe sur `nom`.
   */
  nomTelechargement?: string;
  typeMime: string;
  octets: number;
  salarie: string | null;
  commentaire: string | null;
  classee: boolean;
  deposeeLe: string;
}

/** « 2,4 Mo » — un poids se lit, il ne se compte pas en octets. */
function poids(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} ko`;
  return `${(octets / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`;
}

interface LigneRecue {
  uuid: string;
  ficheUuid: string | null;
  salarie: string | null;
  type: 'absence' | 'heures' | 'prime' | 'autre';
  motif: string | null;
  dateDebut: string | null;
  dateFin: string | null;
  valeur: number | null;
  commentaire: string | null;
  codeVariable: string | null;
  rienASignaler: boolean;
  /**
   * Où en est cette ligne : en attente, portée en paie, ou écartée.
   *
   * Les deux derniers états n'apparaissaient nulle part : la lecture était
   * figée sur « à vérifier ». C'est ce qui rendait les mois passés vides.
   */
  etat: 'a_verifier' | 'portee' | 'ecartee';
  /** Posée par le serveur juste après l'écriture dans OpenPaye ; absente si la ligne a été saisie à la main. */
  envoyeeOpenpayeLe?: string | null;
  createdAt: string;
  /**
   * Ce que l'absence consomme réellement, calculé par le serveur.
   *
   * `null` ou absent quand une donnée manque — régime de l'entreprise inconnu,
   * horaire du salarié inconnu. Rien ne s'affiche alors : c'est la gestionnaire
   * qui portera le chiffre en paie, et un décompte fait dans le mauvais régime
   * annonce 5 là où le droit dit 6.
   */
  joursDecomptes?: number | null;
}

const jour = (d: string) => `${d.slice(8)}/${d.slice(5, 7)}`;

/** Ce que la ligne dit, en une phrase lisible par une gestionnaire. */
function enClair(l: LigneRecue): string {
  if (l.rienASignaler) return 'Rien à signaler ce mois-ci';
  if (l.type === 'absence') {
    const periode =
      l.dateDebut && l.dateFin
        ? l.dateDebut === l.dateFin
          ? jour(l.dateDebut)
          : `${jour(l.dateDebut)} au ${jour(l.dateFin)}`
        : '';
    /*
      LE DÉCOMPTE SE LIT AVEC LA LIGNE. Sans lui, la gestionnaire lisait
      « Congés payés · 03/08 au 07/08 » et devait compter elle-même — alors que
      le client, lui, voyait le chiffre en le saisissant.
    */
    const decompte =
      l.joursDecomptes != null
        ? ` · ${l.joursDecomptes} jour${l.joursDecomptes > 1 ? 's' : ''} décompté${l.joursDecomptes > 1 ? 's' : ''}`
        : '';
    return `${l.motif ?? 'Absence'} · ${periode}${decompte}`;
  }
  if (l.type === 'autre') return l.commentaire ?? l.motif ?? '—';
  const unite = l.type === 'heures' ? 'h' : '';
  return `${l.motif ?? 'Variable'} · ${l.valeur?.toLocaleString('fr-FR') ?? '—'} ${unite}`.trim();
}

export function CeQueLeClientAEnvoye({
  folderUuid,
  mois,
  annee,
  peutEcrireDirectement,
}: {
  folderUuid: string;
  mois: number;
  annee: number;
  peutEcrireDirectement: boolean;
}) {
  const [lignes, setLignes] = useState<LigneRecue[]>([]);
  const [pieces, setPieces] = useState<PieceRecue[]>([]);
  /** Les salariés du dossier, pour proposer un nom plutôt que le faire taper. */
  const [salaries, setSalaries] = useState<string[]>([]);
  const [charge, setCharge] = useState(false);
  const [encours, setEncours] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  /**
   * Ce qui n'est PAS descendu dans la grille, et pourquoi.
   *
   * Une absence ou une ligne sans rubrique ne se porte pas toute seule. Le
   * taire donnerait une liste de « portées » dont une partie n'existe nulle
   * part — la gestionnaire découvrirait le trou en relisant son bulletin.
   */
  const [restant, setRestant] = useState<
    {
      salarie: string | null;
      raison: string;
      rePortable?: boolean;
      issueIncertaine?: boolean;
    }[]
  >([]);

  const recharger = () =>
    Promise.all([
      api
        /*
          `tout: 1` — LE MOIS ENTIER, porté et écarté compris.

          Fiona TAVERNIER, 27/08/2026 : « si tu es en août 2026 et que tu avais
          une prime l'année dernière, tu vas sur août 2025, TU VOIS LA PRIME ».
          Sans ce paramètre, un mois déjà traité s'affichait vide — alors que
          les lignes étaient bien en base.
        */
        .get('/saisie-client/a-verifier', {
          params: { folderUuid, mois, annee, tout: 1 },
        })
        .then(r => setLignes(r.data.data))
        .catch(() => setLignes([])),
      api
        .get('/saisie-client/pieces', { params: { folderUuid, mois, annee } })
        .then(r => setPieces(r.data.data))
        .catch(() => setPieces([])),
      // Les salariés du dossier, tels que la page du client les voit (chez
      // Innov'Paie ils viennent des contrats synchronisés, pas de fiches).
      api
        .get('/saisie-client/salaries', { params: { folderUuid } })
        .then(r =>
          setSalaries(
            ((r.data.data ?? r.data ?? []) as { nom?: string }[])
              .map(f => (f.nom ?? '').trim())
              .filter(Boolean)
              .sort((a: string, b: string) => a.localeCompare(b, 'fr'))
          )
        )
        .catch(() => setSalaries([])),
    ]).finally(() => setCharge(true));

  /**
   * Dire de qui est ce document.
   *
   * Le client dépose sans qu'on lui demande à qui la pièce se rapporte —
   * joindre doit rester aussi simple que dans un mail (décision du 22/08/2026).
   * La gestionnaire, elle, connaît ses salariés : c'est ici que la question se
   * pose utilement, devant le document ouvert. Un nom vide détache.
   */
  const rattacher = async (piece: PieceRecue, salarie: string) => {
    setPieces(ps =>
      ps.map(p =>
        p.uuid === piece.uuid ? { ...p, salarie: salarie || null } : p
      )
    );
    try {
      await api.post(`/saisie-client/pieces/${piece.uuid}/rattacher`, {
        salarie: salarie || null,
      });
    } catch {
      setMessage('Le rattachement n’a pas été enregistré.');
      void recharger();
    }
  };

  /**
   * Ouvrir une pièce.
   *
   * On passe par une requête authentifiée puis un objet local, plutôt que par
   * un lien direct : la route exige un jeton, et un `href` de navigateur ne
   * l'emporte pas. Le document se télécharge, il ne s'affiche pas dans la page.
   */
  const ouvrir = async (piece: PieceRecue) => {
    const r = await api.get(`/saisie-client/pieces/${piece.uuid}/contenu`, {
      responseType: 'blob',
    });
    const adresse = URL.createObjectURL(r.data as Blob);
    const lien = document.createElement('a');
    lien.href = adresse;
    lien.download = nomPourEnregistrer(piece);
    lien.click();
    URL.revokeObjectURL(adresse);
  };

  useEffect(() => {
    setMessage(null);
    void recharger();
  }, [folderUuid, mois, annee]);

  const classer = async (uuids: string[], etat: 'portee' | 'ecartee') => {
    setRestant([]);
    setEncours(true);
    try {
      const r = await api.post('/saisie-client/a-verifier/classer', {
        uuids,
        etat,
      });
      const n = r.data.data.classees;
      if (etat !== 'portee') {
        setMessage(
          `${n} ligne${n > 1 ? 's' : ''} écartée${n > 1 ? 's' : ''}. La trace en est gardée.`
        );
        setRestant([]);
        await recharger();

        return;
      }

      /*
        PORTER, C'EST DESCENDRE LA LIGNE DANS LA GRILLE de saisie des
        variables — depuis le 08/09/2026. Avant, le clic ne changeait qu'un
        état : la gestionnaire retapait ensuite à la main ce que le client
        venait de saisir.

        ⚠️ ON DIT CE QUI N'EST PAS DESCENDU, ET POURQUOI. Une absence, une
        ligne sans rubrique, un salarié pas encore importé : le serveur les
        rend avec leur motif. Annoncer « 4 lignes portées » quand une seule
        est arrivée dans la grille ferait croire au travail fait — c'est
        précisément le défaut qu'on vient de corriger.
      */
      const bilan = r.data.data.portage as
        | {
            portees: number;
            envoyees: number;
            refusees: {
              salarie: string | null;
              raison: string;
              rePortable?: boolean;
              issueIncertaine?: boolean;
            }[];
          }
        | undefined;
      const enPaie = bilan?.envoyees ?? 0;
      const dansLaGrille = bilan?.portees ?? 0;
      setMessage(
        enPaie > 0
          ? `${enPaie} ligne${enPaie > 1 ? 's' : ''} écrite${enPaie > 1 ? 's' : ''} dans OpenPaye` +
              (dansLaGrille > 0
                ? ` — et reprise${dansLaGrille > 1 ? 's' : ''} dans la saisie des variables.`
                : '.')
          : `${n} ligne${n > 1 ? 's' : ''} marquée${n > 1 ? 's' : ''} comme portée${n > 1 ? 's' : ''}.`
      );
      setRestant(bilan?.refusees ?? []);
      await recharger();
    } catch {
      /*
        LE PORTAGE PEUT ÊTRE COUPÉ EN ROUTE (délai du proxy, serveur) : le
        serveur continue, mais le bilan n'arrive pas. On le dit, et on
        recharge : ce qui a été écrit dans OpenPaye porte sa trace, ce qui a
        été refusé est redevenu « à vérifier » — l'écran montre l'état réel
        (verdict QA du 19/09, D3).
      */
      setMessage(
        'La réponse n’est pas arrivée. Rechargez dans un instant : chaque ligne porte son sort — écrite dans OpenPaye, remise « à vérifier », ou « OpenPaye n’a pas répondu » (vérifiez dans OpenPaye avant de ressaisir celle-là).'
      );
      await recharger();
    } finally {
      setEncours(false);
    }
  };

  // Rien reçu : on n'affiche pas un bloc vide tous les mois pour rien.
  if (charge && lignes.length === 0 && pieces.length === 0 && message === null)
    return null;

  // Les lignes sans salarié — commentaire, « rien à signaler » — se lisent en
  // premier : elles parlent du dossier entier.
  const enAttente = lignes.filter(l => l.etat === 'a_verifier');
  const generales = lignes.filter(l => l.salarie === null);
  const parSalarie = new Map<string, LigneRecue[]>();
  lignes
    .filter(l => l.salarie !== null)
    .forEach(l => {
      const nom = l.salarie!;
      parSalarie.set(nom, [...(parSalarie.get(nom) ?? []), l]);
    });

  return (
    <section className="rounded-xl border border-[var(--color-primary-400)] bg-white px-3.5 py-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Inbox className="h-3.5 w-3.5 shrink-0 text-[var(--color-primary-600)]" />
        <span className="text-[12.5px] font-medium text-neutral-900">
          Ce que le client a envoyé
        </span>
        {(lignes.length > 0 || pieces.length > 0) && (
          <span className="text-[11.5px] text-neutral-500">
            {[
              enAttente.length > 0
                ? `${enAttente.length} ligne${enAttente.length > 1 ? 's' : ''} à vérifier`
                : null,
              // Ce qui est traité se compte à part : un mois bouclé doit se
              // lire « rien ne m'attend », pas « douze lignes m'attendent ».
              lignes.length - enAttente.length > 0
                ? `${lignes.length - enAttente.length} déjà traitée${
                    lignes.length - enAttente.length > 1 ? 's' : ''
                  }`
                : null,
              pieces.length > 0
                ? `${pieces.length} document${pieces.length > 1 ? 's' : ''}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
        {enAttente.length > 0 && (
          <button
            type="button"
            disabled={!peutEcrireDirectement || encours}
            onClick={() =>
              void classer(
                enAttente.map(l => l.uuid),
                'portee'
              )
            }
            className="ml-auto flex items-center gap-1.5 rounded-lg bg-[var(--color-primary-600)] px-2.5 py-1 text-[12px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            <Check className="h-3.5 w-3.5" />
            Tout porter en paie
          </button>
        )}
      </div>

      {message && (
        <p className="mb-2 rounded-lg bg-[var(--color-primary-100)] px-3 py-2 text-[12.5px] text-[var(--color-primary-600)]">
          {message}
        </p>
      )}

      {/*
        CE QUI RESTE À FAIRE À LA MAIN, nommé ligne par ligne. En ambre et non
        en rouge : ce n'est pas une erreur, c'est du travail qui revient à
        quelqu'un.
      */}
      {restant.length > 0 && (
        <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">
          {/*
            TROIS SORTS, TROIS PHRASES (re-verdict HUP R1, 20/09/2026). Tout
            dire « à saisir vous-même » faisait retaper une ligne que le serveur
            venait de REMETTRE « à vérifier » — puis « Tout porter » la
            reportait : une absence en double dans la paie. Et une ligne à
            issue INCERTAINE (OpenPaye n'a pas répondu) ne se ressaisit qu'après
            avoir regardé dans OpenPaye : la même absence postée deux fois y
            fait deux absences (mesuré le 20/09).
          */}
          <ul className="space-y-0.5">
            {restant.map((r, i) => (
              <li key={i} className="flex gap-1.5">
                <span className="text-amber-400">•</span>
                <span>
                  {r.salarie ? <b>{r.salarie}</b> : 'Sans salarié'} —{' '}
                  {phraseDuSort(r)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-col gap-1">
        {generales.map(l => (
          <Ligne
            key={l.uuid}
            ligne={l}
            peutEcrireDirectement={peutEcrireDirectement}
            encours={encours}
            onClasser={classer}
          />
        ))}

        {[...parSalarie.entries()].map(([nom, siennes]) => (
          <div
            key={nom}
            className="rounded-lg border border-[var(--color-primary-100)] px-3 py-2"
          >
            <p className="mb-1 text-[12.5px] font-medium text-neutral-900">
              {nom}
            </p>
            <div className="flex flex-col gap-0.5">
              {siennes.map(l => (
                <Ligne
                  key={l.uuid}
                  ligne={l}
                  compacte
                  peutEcrireDirectement={peutEcrireDirectement}
                  encours={encours}
                  onClasser={classer}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Les documents déposés, sous les lignes qu'ils justifient. Le nom est
          celui donné par le client : c'est du texte, il ne sert jamais à
          ouvrir le fichier — voir le service côté API. */}
      {pieces.length > 0 && (
        <div className="mt-2.5 border-t border-[var(--color-primary-100)] pt-2.5">
          <p className="mb-1.5 text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
            Documents reçus
          </p>
          <div className="flex flex-col gap-1">
            {pieces.map(p => (
              <div
                key={p.uuid}
                className="group flex items-center gap-2.5 rounded-lg border border-[var(--color-primary-100)] px-3 py-1.5"
              >
                <FileText className="h-3.5 w-3.5 shrink-0 text-[var(--color-primary-600)]" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12.5px] text-neutral-900">
                    {p.nom}
                  </span>
                  {/*
                    DE QUI EST CE DOCUMENT ? La question ne peut pas être posée
                    au client — on lui a retiré pour que joindre reste aussi
                    simple que dans un mail. Elle se pose donc ici, à qui
                    connaît les salariés. Un menu plutôt qu'un champ libre :
                    « BOUZID » et « Bouzid Naïma » ne se rattachent pas au même
                    endroit, et c'est le genre d'écart qui se paie plus tard.
                  */}
                  <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-neutral-400">
                    <select
                      value={p.salarie ?? ''}
                      onChange={e => void rattacher(p, e.target.value)}
                      aria-label={`De qui est ${p.nom} ?`}
                      className={`max-w-[190px] truncate rounded border border-transparent bg-transparent py-0.5 text-[11px] hover:border-[var(--color-primary-100)] focus:border-[var(--color-primary-400)] focus:outline-none ${
                        p.salarie
                          ? 'text-neutral-600'
                          : 'text-neutral-400 italic'
                      }`}
                    >
                      <option value="">toute l’entreprise</option>
                      {salaries.map(s => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                      {/* Un nom venu d'ailleurs ne doit pas disparaître du menu. */}
                      {p.salarie && !salaries.includes(p.salarie) && (
                        <option value={p.salarie}>{p.salarie}</option>
                      )}
                    </select>
                    <span aria-hidden>·</span>
                    <span>{poids(p.octets)}</span>
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => void ouvrir(p)}
                  aria-label={`Ouvrir ${p.nom}`}
                  title="Ouvrir le document"
                  className="shrink-0 rounded p-1 text-neutral-400 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-600)] focus:opacity-100 max-md:opacity-100"
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Ligne({
  ligne,
  compacte,
  peutEcrireDirectement,
  encours,
  onClasser,
}: {
  ligne: LigneRecue;
  compacte?: boolean;
  peutEcrireDirectement: boolean;
  encours: boolean;
  onClasser: (uuids: string[], etat: 'portee' | 'ecartee') => Promise<void>;
}) {
  /*
    UNE LIGNE DÉJÀ TRAITÉE SE LIT, ELLE NE SE REJOUE PAS.

    C'est ce qui rend les mois passés consultables sans les rendre modifiables :
    on retrouve ce que le client avait déclaré ET ce que le cabinet en avait
    fait, mais les deux boutons disparaissent. Reclasser un mois bouclé n'a
    aucun sens, et un bouton qui ne devrait jamais servir finit par servir.
  */
  const traitee = ligne.etat !== 'a_verifier';

  return (
    <div
      className={`group flex items-center gap-2 ${
        compacte
          ? 'py-0.5'
          : 'rounded-lg border border-[var(--color-primary-100)] px-3 py-2'
      }`}
    >
      <span
        className={`min-w-0 flex-1 truncate text-[12.5px] ${
          // Écartée : barrée. C'est la seule façon de voir d'un coup d'œil que
          // cette prime-là n'est PAS partie en paie.
          ligne.etat === 'ecartee'
            ? 'text-neutral-400 line-through'
            : traitee
              ? 'text-neutral-500'
              : 'text-neutral-700'
        }`}
      >
        {enClair(ligne)}
      </span>

      {traitee && (
        <span className="shrink-0 rounded-full bg-[#ecf4f5] px-2 py-0.5 text-[10.5px] text-neutral-500">
          {/* « Écrite dans OpenPaye le … » ≠ « portée » (saisie à la main) : la trace vient du serveur. */}
          {etiquetteTraitee(ligne)}
        </span>
      )}

      {/* Une ligne sans rubrique ne peut pas être portée sans décision : on le
          dit ici, pas dans un écran d'erreurs à part. */}
      {!traitee &&
        ligne.type !== 'absence' &&
        ligne.type !== 'autre' &&
        ligne.codeVariable === null && (
          <span className="shrink-0 rounded-full bg-[#FAEEDA] px-2 py-0.5 text-[10.5px] text-[#854F0B]">
            à qualifier
          </span>
        )}

      {!traitee && (
        <span className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 max-md:opacity-100">
          <button
            type="button"
            disabled={!peutEcrireDirectement || encours}
            onClick={() => void onClasser([ligne.uuid], 'portee')}
            title="Portée en paie"
            aria-label={`Marquer comme portée : ${enClair(ligne)}`}
            className="rounded p-1 text-neutral-400 hover:bg-[var(--color-primary-100)] hover:text-[var(--color-primary-600)] disabled:opacity-40"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            disabled={!peutEcrireDirectement || encours}
            onClick={() => void onClasser([ligne.uuid], 'ecartee')}
            title="Écarter — la trace est gardée"
            aria-label={`Écarter : ${enClair(ligne)}`}
            className="rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      )}
    </div>
  );
}
